/**
 * Asking the backend whether it is there.
 *
 * Deliberately not the generated client. That one attaches an access token,
 * refreshes it when the server refuses, and raises the global error toast -- all
 * correct for the application and all wrong here, where the interesting answers
 * are "no session" and "the request never arrived", and where a refresh attempt
 * would replace the failure being diagnosed with a different one.
 *
 * `/api/settings/` is the endpoint asked because it is unauthenticated: a 200
 * says the address is right, the proxy is passing `/api/` through, and CORS
 * allows this origin -- which is the whole of what `/debug` exists to answer.
 */
export interface ProbeResult {
  /** The status, or `null` when the request never reached a server at all. */
  status: number | null
  ok: boolean
  milliseconds: number
  /** The browser's own words, for the case where there is no status to report. */
  error: string | null
}

export async function probeBackend(
  origin: string,
  fetcher: typeof fetch = globalThis.fetch,
  now: () => number = () => performance.now(),
): Promise<ProbeResult> {
  const started = now()
  try {
    const response = await fetcher(`${origin}/api/settings/`, {
      method: 'GET',
      // No credentials and no headers: anything else could turn a plain request
      // into a preflighted one and fail for a reason that is not the deployment.
      credentials: 'omit',
    })
    return {
      status: response.status,
      ok: response.ok,
      milliseconds: Math.round(now() - started),
      error: null,
    }
  } catch (error) {
    // A network-level failure carries no status, and its message is the only
    // evidence there is of whether this was DNS, TLS, CORS or a refused port.
    return {
      status: null,
      ok: false,
      milliseconds: Math.round(now() - started),
      error: error instanceof Error ? error.message : String(error),
    }
  }
}
